import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { base44 } from "@/api/base44Client";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { ChevronLeft, ChevronRight, Plus, X, CalendarClock, Loader2, AlertCircle } from "lucide-react";
import { format, startOfMonth, endOfMonth, startOfWeek, addDays, isSameMonth, isSameDay, parseISO, isWithinInterval } from "date-fns";
import BookSessionModal from "@/components/trainer/BookSessionModal";

const STATUS_COLORS = {
  upcoming: "bg-primary/10 text-primary",
  completed: "bg-green-100 text-green-700",
  cancelled: "bg-red-100 text-red-600 line-through",
};

function RescheduleForm({ session, sessions, onSave, onCancel }) {
  const [date, setDate] = useState(session.date ? format(parseISO(session.date), "yyyy-MM-dd'T'HH:mm") : "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // Check if trainer has a conflict at the new time
  const checkTrainerConflict = () => {
    if (!date || !session.trainer) return false;
    
    const sessionStart = parseISO(date);
    const sessionEnd = new Date(sessionStart.getTime() + (session.duration_minutes || 60) * 60000);
    
    return sessions.some(s => {
      if (s.id === session.id) return false; // Skip self
      if (s.status === "cancelled") return false;
      if (s.trainer !== session.trainer) return false;
      
      const existingStart = parseISO(s.date);
      const existingEnd = new Date(existingStart.getTime() + (s.duration_minutes || 60) * 60000);
      
      return isWithinInterval(sessionStart, { start: existingStart, end: existingEnd }) ||
             isWithinInterval(sessionEnd, { start: existingStart, end: existingEnd }) ||
             isWithinInterval(existingStart, { start: sessionStart, end: sessionEnd });
    });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setError("");
    
    if (checkTrainerConflict()) {
      setError("Trainer already has a session booked at this time.");
      return;
    }
    
    setSaving(true);
    await base44.entities.TrainingSession.update(session.id, { date, status: "upcoming" });
    setSaving(false);
    onSave();
  };

  return (
    <form onSubmit={handleSave} className="space-y-3 mt-2">
      {error && (
        <div className="flex gap-2 items-start rounded-lg bg-destructive/10 p-3 text-destructive text-sm">
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
          <p>{error}</p>
        </div>
      )}
      <div>
        <Label>New Date & Time</Label>
        <Input type="datetime-local" value={date} onChange={e => setDate(e.target.value)} required />
      </div>
      <div className="flex gap-2">
        <Button type="button" variant="outline" className="flex-1" onClick={onCancel}>Cancel</Button>
        <Button type="submit" className="flex-1" disabled={saving}>
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Reschedule"}
        </Button>
      </div>
    </form>
  );
}

export default function ClientSessionsDialog({ client, open, onOpenChange }) {
  const queryClient = useQueryClient();
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [showBook, setShowBook] = useState(false);
  const [rescheduleSession, setRescheduleSession] = useState(null);
  const [sessionToCancel, setSessionToCancel] = useState(null);

  const { data: sessions = [], isLoading } = useQuery({
    queryKey: ["clientSessions", client?.id],
    queryFn: () => base44.entities.TrainingSession.filter({ sessionClientId: client.id }, "-date", 200),
    enabled: !!client && open,
  });

  const { data: trainers = [] } = useQuery({
    queryKey: ["trainers-active"],
    queryFn: () => base44.entities.Trainer.filter({ is_active: true }),
    enabled: !!client && open,
  });

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ["clientSessions", client?.id] });
    setShowBook(false);
    setRescheduleSession(null);
  };

  const confirmCancelSession = async () => {
    if (!sessionToCancel) return;
    await base44.entities.TrainingSession.update(sessionToCancel.id, { status: "cancelled" });
    setSessionToCancel(null);
    refresh();
  };

  // Build calendar grid
  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const calStart = startOfWeek(monthStart, { weekStartsOn: 0 });
  const days = [];
  let d = calStart;
  while (d <= monthEnd || days.length % 7 !== 0) {
    days.push(d);
    d = addDays(d, 1);
  }

  const sessionsByDate = {};
  sessions.forEach(s => {
    if (!s.date) return;
    const key = format(parseISO(s.date), "yyyy-MM-dd");
    if (!sessionsByDate[key]) sessionsByDate[key] = [];
    sessionsByDate[key].push(s);
  });

  const selectedDateKey = selectedDate ? format(selectedDate, "yyyy-MM-dd") : null;
  const selectedSessions = selectedDateKey ? (sessionsByDate[selectedDateKey] || []) : [];
  const hasSessionOnSelected = selectedSessions.some(s => s.status !== "cancelled");

  if (!client) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto w-[calc(100%-2rem)] md:w-full rounded-xl md:rounded-lg">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <CalendarClock className="w-5 h-5 text-primary" />
            Sessions — {client.full_name || client.email}
          </DialogTitle>
        </DialogHeader>

        {/* Calendar */}
        <div className="rounded-xl border bg-card p-4">
          {/* Month nav */}
          <div className="flex items-center justify-between mb-4">
            <Button variant="ghost" size="icon" onClick={() => setCurrentMonth(m => addDays(startOfMonth(m), -1))}>
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="font-semibold text-sm">{format(currentMonth, "MMMM yyyy")}</span>
            <Button variant="ghost" size="icon" onClick={() => setCurrentMonth(m => addDays(endOfMonth(m), 1))}>
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>

          {/* Day headers */}
          <div className="grid grid-cols-7 mb-1">
            {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(d => (
              <div key={d} className="text-center text-xs text-muted-foreground font-medium py-1">{d}</div>
            ))}
          </div>

          {/* Day cells */}
          {isLoading ? (
            <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
          ) : (
            <div className="grid grid-cols-7 gap-1">
              {days.map((day, i) => {
                const key = format(day, "yyyy-MM-dd");
                const daysSessions = sessionsByDate[key] || [];
                const isSelected = selectedDate && isSameDay(day, selectedDate);
                const isCurrentMonth = isSameMonth(day, currentMonth);
                const hasActive = daysSessions.some(s => s.status !== "cancelled");

                return (
                  <button
                    key={i}
                    onClick={() => { setSelectedDate(day); setShowBook(false); setRescheduleSession(null); }}
                    className={`
                      relative aspect-square flex flex-col items-center justify-start pt-1 rounded-lg text-xs transition-colors
                      ${!isCurrentMonth ? "opacity-30" : ""}
                      ${isSelected ? "bg-primary text-primary-foreground" : "hover:bg-muted"}
                    `}
                  >
                    <span className="font-medium">{format(day, "d")}</span>
                    {hasActive && (
                      <span className={`w-1.5 h-1.5 rounded-full mt-0.5 ${isSelected ? "bg-primary-foreground" : "bg-primary"}`} />
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Selected date panel */}
        {selectedDate && (
          <div className="rounded-xl border bg-card p-4 space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-sm">{format(selectedDate, "EEEE, MMMM d")}</h3>
              {!hasSessionOnSelected && !showBook && (
                <Button size="sm" className="gap-1" onClick={() => { setShowBook(true); setRescheduleSession(null); }}>
                  <Plus className="w-3.5 h-3.5" /> Book Session
                </Button>
              )}
            </div>

            {selectedSessions.length === 0 && !showBook && (
              <p className="text-sm text-muted-foreground">No sessions on this day. Click "Book Session" to schedule one.</p>
            )}

            {selectedSessions.map(session => (
              <div key={session.id} className="rounded-lg border p-3 space-y-1">
                {rescheduleSession?.id === session.id ? (
                   <RescheduleForm session={session} sessions={sessions} onSave={refresh} onCancel={() => setRescheduleSession(null)} />
                 ) : (
                  <>
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="font-semibold text-sm">{session.title}</p>
                        <p className="text-xs text-muted-foreground">
                          {session.date ? format(parseISO(session.date), "h:mm a") : "—"}
                          {session.duration_minutes ? ` · ${session.duration_minutes} min` : ""}
                        </p>
                      </div>
                      <Badge className={`text-xs shrink-0 ${STATUS_COLORS[session.status]}`}>{session.status}</Badge>
                    </div>
                    {session.status !== "cancelled" && (
                      <div className="flex gap-2 pt-1">
                        <Button
                          size="sm" variant="outline" className="text-xs h-7"
                          onClick={() => { setRescheduleSession(session); setShowBook(false); }}
                        >
                          Reschedule
                        </Button>
                        <Button
                           size="sm" variant="outline" className="text-xs h-7 text-destructive border-destructive/40 hover:bg-destructive/10"
                           onClick={() => setSessionToCancel(session)}
                         >
                           <X className="w-3 h-3 mr-1" /> Cancel
                         </Button>
                      </div>
                    )}
                  </>
                )}
              </div>
            ))}
          </div>
        )}

        <AlertDialog open={!!sessionToCancel} onOpenChange={(o) => { if (!o) setSessionToCancel(null); }}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Cancel Session?</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to cancel this session? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Keep Session</AlertDialogCancel>
              <AlertDialogAction onClick={confirmCancelSession} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                Cancel Session
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <BookSessionModal
          open={showBook}
          onOpenChange={setShowBook}
          client={client}
          date={selectedDate}
          trainers={trainers}
          sessions={sessions}
          onSuccess={refresh}
        />
      </DialogContent>
    </Dialog>
  );
}